import {
  getSliceSelectionDOMId,
  SelectionManagerProvider,
  type SliceSelection,
  type SliceSelectionArea,
  SliceSelectionContext,
  type SliceSelectionUpdateArgs,
  useSelectionManager,
} from "@prismicio/editor-fields"
import {
  getOffsetTopRelativeTo,
  getScrollableAncestor,
} from "@prismicio/editor-support/DOM"
import { useStableCallback } from "@prismicio/editor-support/React"
import {
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react"

import { useContentOffset } from "../app/layout/useOffset"
import { headerBottomMargin } from "./documentLayout"
import { useDocumentParams, useGoToDocumentSelection } from "./documentRouting"

interface Props {
  children: ReactNode
}

/**
 * Manages the SliceTable and DocumentEditor bi-directional selection.
 */
export function SliceSelectionProvider(props: Props) {
  const { children } = props

  const { selectionId } = useDocumentParams()

  const initialState = selectionId
    ? { selectedIds: new Set([selectionId]) }
    : undefined

  return (
    <SelectionManagerProvider initialState={initialState}>
      <SelectionProviderImpl>{children}</SelectionProviderImpl>
    </SelectionManagerProvider>
  )
}

function SelectionProviderImpl(props: Props) {
  const { children } = props

  const selectionManager = useSelectionManager()
  const { isSelected, getSelectionState } = selectionManager
  const { selectedIds } = getSelectionState()
  const firstId = selectedIds[0]

  const [scrollRequest, setScrollRequest] = useState<ScrollRequest>({
    requestedFrom: "all",
  })

  const update = useStableCallback((args: SliceSelectionUpdateArgs) => {
    const { id, requestedFrom, metaKey, shiftKey, ids } = args

    // Do not allow unselecting the last slice in the table: It serves no purpose.
    const isLastSelectedSliceInTable =
      requestedFrom === "sliceTable" &&
      id === firstId &&
      selectedIds.length === 1

    // When focusing a slice in the editor, we do NOT want to toggle but only keep selected.
    const isAlreadyFocused = requestedFrom === "sliceFieldSet" && isSelected(id)

    const shouldToggle = !isLastSelectedSliceInTable && !isAlreadyFocused

    if (shouldToggle)
      selectionManager.toggleSelection({
        isSingleSelection: true,
        id,
        metaKey,
        shiftKey,
        ids,
      })

    setScrollRequest({ requestedFrom })
  })

  const selectionValue: SliceSelection = useMemo(
    () => ({ ids: selectedIds, update, isSelected }),
    [selectedIds, update, isSelected],
  )

  useScrollToSelection("sliceTable", firstId, scrollRequest)
  useScrollToSelection("sliceFieldSet", firstId, scrollRequest)

  // The SelectionManager is the source of truth.
  // We also keep the URL in sync, so refreshing/bookmarking works.
  useUpdateUrlFromSelection(firstId)

  return (
    <SliceSelectionContext.Provider value={selectionValue}>
      {children}
    </SliceSelectionContext.Provider>
  )
}

// As an object because we do want to force the scroll effect even if none of the underlying data changed,
// because the scroll position might have been manually changed by the user in the meantime.
interface ScrollRequest {
  requestedFrom: SliceSelectionUpdateArgs["requestedFrom"]
}

function useScrollToSelection(
  area: SliceSelectionArea,
  id: string | undefined,
  scrollRequest: ScrollRequest,
) {
  const { contentOffset } = useContentOffset()

  useLayoutEffect(() => {
    const { requestedFrom } = scrollRequest
    if (!id) return

    // If the selection request comes from one side, the intent is to highlight the other side.
    if (requestedFrom === area) return

    const elementId = getSliceSelectionDOMId(area, id)
    if (!elementId) return

    const element = document.getElementById(elementId)
    if (!element) return

    const scrollParent = getScrollableAncestor(element)
    if (!scrollParent) return

    const top = getOffsetTopRelativeTo(element, scrollParent)

    const offset =
      headerBottomMargin + (area === "sliceFieldSet" ? contentOffset : 0)

    const targetScrollTop = top - offset

    if (scrollParent.scrollTop === targetScrollTop) return

    scrollParent.scrollTop = targetScrollTop
  }, [area, contentOffset, id, scrollRequest])
}

function useUpdateUrlFromSelection(id: string | undefined) {
  const goToDocumentSelection = useGoToDocumentSelection()

  useEffect(() => {
    goToDocumentSelection(id)
  }, [id, goToDocumentSelection])
}
